import { Dialog, DialogSurface, DialogBody, DialogTitle, Button, DialogContent, Field, Select, Table, TableCell, TableHeader, TableHeaderCell, TableRow, Tooltip, tokens, MessageBar, MessageBarBody, Switch } from "@fluentui/react-components";
import { Add20Regular, Dismiss24Filled, DismissRegular } from "@fluentui/react-icons";
import React from "react";
import { IShareSubscriptionPostModel, OwnerUsesBillingInstancePostModel, ShareDefinitionDestinationType, ShareSubscriptionPostModel, StopSharingSubscriptionPostModel, SubscriptionShareResponseModel } from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { ContactPicker } from "../../files/share/contact-picker/contact-picker.component";
import { useForm } from "react-hook-form";
import { getLicensingClient, getSubscriptionsClient } from "../../services/dashboard.service";
import { processServerError } from "../../utils/helpers/error.helper";
import { toast } from "react-toastify";
import { TableBodyWithLoading } from "s365-dashboard-v2-file-picker";
import { PersonDisplay } from "../../components/person-display/person-display.component";
import ShowLocalTime from "../../components/show-local-time/show-local-time.component";
import { AddContactDialog } from "../../contacts/my-contacts/add-contact-dialog.component";

type ShareSubscriptionModalProps = {
    isOpened: boolean;
    subscriptionId: number;
    billingInstanceId: number;
    isOrganizationLicense: boolean;
    maxUsers?: number;
    ownerCantUseLicense: boolean;
    onClose: () => void;
}
type ShareWithOption = {
    value: ShareDefinitionDestinationType,
    label: string
}
const defaultShareWithOptions: ShareWithOption[] = [{ value: ShareDefinitionDestinationType.User, label: "Contact" }];

export const ShareSubscriptionModal: React.FC<ShareSubscriptionModalProps> = (props) => {
    const [open, setOpen] = React.useState<boolean>(false);
    const [shareWith, setShareWith] = React.useState<ShareDefinitionDestinationType>(ShareDefinitionDestinationType.User);
    const [shareWithOptions, setShareWithOptions] = React.useState<ShareWithOption[]>(defaultShareWithOptions);
    const [isSubmitRequestInProgress, setIsSubmitRequestInProgress] = React.useState<boolean>(false);
    const [sharedDefinitions, setSharedDefinitions] = React.useState<SubscriptionShareResponseModel[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [ownerCantUseLoading, setOwnerCantUseLoading] = React.useState<boolean>(false);
    const [showAddContactModal, setShowAddContactModal] = React.useState<boolean>(false);

    const { register, handleSubmit, watch, setError, formState: { errors }, setValue } = useForm<IShareSubscriptionPostModel>({
        defaultValues: {
            subscriptionId: props.subscriptionId
        }
    });
    React.useEffect(() => { setOpen(!!props.isOpened); }, [props.isOpened]);

    React.useEffect(() => { getSharedWith(); }, []);

    const getSharedWith = async () => {
        try {
            setIsLoading(true);
            const client = getLicensingClient();
            const sharedWithListResp = await client.getSubscriptionSharedWith(props.subscriptionId);
            setSharedDefinitions(sharedWithListResp ?? []);
        } catch (error) {
            processServerError(error, undefined, "An error occurred while getting subscription shared with list.");
        } finally {
            setIsLoading(false);
        }
    }

    const onModalClose = () => {
        setOpen(false);
        props.onClose();
    }

    const onShareWithChange = () => {
        // Currently we allow only sharing with Contacts and Organization members
    }

    const onStopSharingClick = async (item: SubscriptionShareResponseModel) => {
        try {
            setIsLoading(true);
            const client = getLicensingClient();
            await client.stopSharing(new StopSharingSubscriptionPostModel({
                subscriptionId: item.subscriptionId,
                userId: +item.sharedWithUserId
            }));
            toast.success("Subscription sharing has been stopped.");
            getSharedWith();
        } catch (error) {
            processServerError(error, undefined, "An error occurred while stoping sharing subscription.");
        } finally {
            setIsLoading(false);
        }

    }

    const onSubmit = async (data: IShareSubscriptionPostModel) => {

        console.log("Sharing with", data);

        try {
            setIsSubmitRequestInProgress(true);
            const client = getLicensingClient();
            const model = new ShareSubscriptionPostModel({
                subscriptionId: data.subscriptionId,
                userId: data.userId
            });
            await client.shareSubscription(model);
            toast.success("Subscription shared successfully.");
            getSharedWith();

        } catch (error) {
            processServerError(error, undefined, `An error occurred while sharing subscription.`);
        } finally {
            setIsSubmitRequestInProgress(false);
        }
    }

    const onOwnerCantUseChanged = async (ownerCantUse: boolean) => {
        try {
            setOwnerCantUseLoading(true);
            const client = getSubscriptionsClient();
            const model = new OwnerUsesBillingInstancePostModel({
                billingInstanceId: props.billingInstanceId,
                ownerCantUse: ownerCantUse
            });
            await client.changeOwnerUsesSubscription(model);
        } catch (error) {
            processServerError(error, undefined, "An error occurred while changing status of owner using subscription.");
        } finally {
            setOwnerCantUseLoading(false);
        }
    }

    return <><Dialog open={open} onOpenChange={(event, data) => {

        setOpen(data.open);
        if (!data.open) {
            onModalClose();
        }
    }}>
        <DialogSurface style={{ width: "70vw", maxWidth: "900px" }}>

            <DialogBody style={{ maxWidth: "100%" }}>
                <DialogTitle action={<Button appearance="transparent" onClick={onModalClose} icon={<Dismiss24Filled />} />}>
                    Share or assign subscriptions
                </DialogTitle>
                <DialogContent style={{ height: "80vh" }}>
                    <MessageBar key={"info"} intent={'info'} style={{ margin: "5px" }} className="alert--info-global">
                        <MessageBarBody style={{ lineHeight: "1rem" }}>
                            {!!props.maxUsers && <span>You can assign the subscription seats to {props.maxUsers} individual users within your organization.</span>}
                            {!props.maxUsers && <span>View and manage contacts under Collaboration.</span>}
                        </MessageBarBody>
                    </MessageBar>
                    {props.isOrganizationLicense && <div><Switch checked={props.ownerCantUseLicense}
                        labelPosition="after"
                        label={<i>I have purchased DWSIM Pro subscription but I don't use the software. I understand that I don't have a DWSIM Pro usage available.</i>}
                        onChange={(ev, data) => { onOwnerCantUseChanged(data.checked); }} /></div>}

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                            <Field
                                label="Share with"
                                style={{ width: "150px" }}
                            >  <Select onChange={onShareWithChange}>
                                    {shareWithOptions.map((item) => {
                                        return <option value={item.value}>{item.label}</option>
                                    })}


                                </Select>
                            </Field>
                            {shareWith === ShareDefinitionDestinationType.User &&
                                <Field style={{ marginTop: "24px" }} validationState={errors.userId ? "error" : undefined} validationMessage={!!errors.userId ? "User is required." : undefined}>
                                    <ContactPicker
                                        placeholder="Find contact"
                                        showOnlyOrganizationMembers={props.isOrganizationLicense}
                                        style={{ width: "300px" }}
                                        {...register("userId", { required: { value: true, message: "User is required." } })}
                                        onChange={(contact) => {
                                            setValue("userId", +contact.value?.toString());
                                        }} />
                                </Field>
                            }
                            <Button appearance="primary" type="submit" disabled={isSubmitRequestInProgress || ownerCantUseLoading} style={{ marginTop: "24px" }} >Share</Button>
                            <Button style={{ minWidth: "110px", marginTop: "24px" }}
                                onClick={() => { setShowAddContactModal(true); }}
                                appearance='primary'
                                icon={<Add20Regular />}>Add Contact</Button>
                        </div>
                    </form>


                    <div style={{ maxHeight: "calc(80vh - 80px)", overflowY: "auto", marginTop: "var(--spacingVerticalXL)" }}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell key="displayName" className='groups-table__cell--bold'>Display name</TableHeaderCell>
                                    <TableHeaderCell key="sharedAt" style={{ width: "200px" }} className='groups-table__cell--bold  column--center'>Shared</TableHeaderCell>
                                    <TableHeaderCell key="actions" style={{ width: "50px" }} className='groups-table__cell--bold  column--center'></TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBodyWithLoading isLoading={isLoading}
                                columnCount={2} loadingMessage="Loading..."
                                itemCount={sharedDefinitions ? sharedDefinitions.length : 0}
                                noItemsMessage="No results.">
                                {sharedDefinitions && sharedDefinitions.length > 0 && sharedDefinitions.map((item, index) => {


                                    return <TableRow>

                                        <TableCell>
                                            <PersonDisplay id={item.user?.id?.toString()} name={item.user?.displayName} userPrincipalName={item.user?.userPrincipalName} />
                                        </TableCell>
                                        <TableCell className="column--center">
                                            <ShowLocalTime date={item.createdAtUtc} multiline />
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: "flex", alignItems: "end", flexDirection: "column", maxWidth: "50px" }}>
                                                <Tooltip content="Stop sharing" relationship="label">
                                                    <Button
                                                        appearance="transparent"
                                                        disabled={isSubmitRequestInProgress}
                                                        onClick={() => { onStopSharingClick(item) }}
                                                        icon={<DismissRegular primaryFill="red" />}></Button>
                                                </Tooltip>
                                            </div>

                                        </TableCell>

                                    </TableRow>
                                })}
                            </TableBodyWithLoading>
                        </Table>

                    </div>
                </DialogContent>
            </DialogBody>
        </DialogSurface>
    </Dialog>
        {showAddContactModal &&
            <AddContactDialog
                isOpened={showAddContactModal}
                onSuccess={() => { setShowAddContactModal(false); }}
                onClose={() => { setShowAddContactModal(false); }} />}</>
}